import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    FiCheckCircle,
    FiEdit2,
    FiPackage,
    FiPlus,
    FiRefreshCw,
    FiTrash2,
    FiUsers,
} from "react-icons/fi";


import BusinessModal
    from "../../business/components/BusinessModal";

import useSwal
    from "../../../../../../hooks/useSwal";

import {
    fetchPlatformSubscriptionPlans,
    createPlatformSubscriptionPlan,
    updatePlatformSubscriptionPlan,
    deletePlatformSubscriptionPlan,
    fetchPlatformSubscriptionModules,
    syncPlatformTenantPlanAccess,

    selectPlatformSubscriptionPlans,
    selectPlatformSubscriptionPlanLoading,
    selectPlatformSubscriptionPlanSaving,
    selectPlatformSubscriptionModules,
    selectPlatformSubscriptionLegacyModuleCodeMap,
    selectPlatformSubscriptionModulesLoading,
    selectPlatformSubscriptionModulesError,
    selectPlatformTenantPlanSyncing,
} from "../../../../../../store/platformSubscriptionPlan/platformSubscriptionPlanSlice";

import "./PlatformSubscriptionPlan.css";



const EMPTY_FORM = {
    name: "",
    code: "",
    planType: "PAID",
    description: "",
    badgeText: "",
    isPopular: false,

    currency: "INR",

    monthlyPrice: 0,
    yearlyPrice: 0,
    yearlyDiscountPercent: 10,

    customPricing: false,

    durationType: "MONTHS",
    durationValue: 1,
    trialDays: 0,

    allowedBillingCycles: [
        "MONTHLY",
        "YEARLY",
    ],

    maxEmployees: 10,
    maxProductListings: 200,
    maxBranches: 1,
    maxStorageGB: 10,

    storefrontEnabled: true,
    marketplaceEnabled: true,
    apiAccessEnabled: false,
    prioritySupport: false,
    dedicatedAccountManager: false,

    modules: [],

    availableForSignup: true,
    autoAssignOnTenantRegistration: false,

    sortOrder: 0,
    status: "ACTIVE",
};


const PlatformSubscriptionPlanList =
    () => {

        const dispatch =
            useDispatch();


        const swal =
            useSwal();


        const plans =
            useSelector(
                selectPlatformSubscriptionPlans
            );


        const loading =
            useSelector(
                selectPlatformSubscriptionPlanLoading
            );


        const saving =
            useSelector(
                selectPlatformSubscriptionPlanSaving
            );


        const moduleOptions =
            useSelector(
                selectPlatformSubscriptionModules
            );


        const legacyModuleCodeMap =
            useSelector(
                selectPlatformSubscriptionLegacyModuleCodeMap
            );


        const modulesLoading =
            useSelector(
                selectPlatformSubscriptionModulesLoading
            );


        const modulesError =
            useSelector(
                selectPlatformSubscriptionModulesError
            );


        const syncingTenants =
            useSelector(
                selectPlatformTenantPlanSyncing
            );


        const [
            showModal,
            setShowModal,
        ] =
            useState(false);


        const [
            editingPlan,
            setEditingPlan,
        ] =
            useState(null);


        const [
            form,
            setForm,
        ] =
            useState(
                EMPTY_FORM
            );


        useEffect(
            () => {

                dispatch(
                    fetchPlatformSubscriptionPlans()
                );


                dispatch(
                    fetchPlatformSubscriptionModules()
                );

            },
            [
                dispatch,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | NORMALIZE EXISTING SAVED MODULE CODE
        |--------------------------------------------------------------------------
        */

        const normalizeSavedModuleCode =
            (
                value
            ) => {

                const code =
                    String(
                        value ||
                        ""
                    )
                        .trim()
                        .toUpperCase();


                return (
                    legacyModuleCodeMap[
                        code
                    ] ||
                    code
                );

            };


        /*
        |--------------------------------------------------------------------------
        | BUILD FORM MODULES FROM API
        |--------------------------------------------------------------------------
        */

        const buildFormModules =
            (
                savedModules = []
            ) => {

                const savedMap =
                    new Map();


                (
                    Array.isArray(
                        savedModules
                    )
                        ?
                        savedModules
                        :
                        []
                )
                    .forEach(
                        (
                            saved
                        ) => {

                            const code =
                                normalizeSavedModuleCode(
                                    saved?.code
                                );


                            if (
                                !code
                            ) {

                                return;

                            }


                            const previous =
                                savedMap.get(
                                    code
                                );


                            if (
                                !previous
                            ) {

                                savedMap.set(
                                    code,
                                    saved
                                );

                                return;

                            }


                            savedMap.set(
                                code,
                                {
                                    ...previous,
                                    ...saved,

                                    enabled:
                                        previous?.enabled ===
                                            true ||
                                        saved?.enabled ===
                                            true,

                                    permissions: [
                                        ...new Set([
                                            ...(
                                                previous
                                                    ?.permissions ||
                                                []
                                            )
                                                .map(
                                                    String
                                                ),

                                            ...(
                                                saved
                                                    ?.permissions ||
                                                []
                                            )
                                                .map(
                                                    (
                                                        permission
                                                    ) =>
                                                        String(
                                                            permission
                                                                ?._id ||
                                                            permission
                                                        )
                                                ),
                                        ]),
                                    ],
                                }
                            );

                        }
                    );


                return (
                    moduleOptions ||
                    []
                )
                    .map(
                        (
                            module
                        ) => {

                            const existing =
                                savedMap.get(
                                    module.code
                                );


                            return {
                                ...module,

                                enabled:
                                    Boolean(
                                        existing
                                            ?.enabled
                                    ),

                                permissions:
                                    existing
                                        ?.permissions ||
                                    [],
                            };

                        }
                    );

            };


        const buildCreateForm =
            () => ({
                ...EMPTY_FORM,

                modules:
                    buildFormModules(),
            });


        const orderedPlans =
            useMemo(
                () => {

                    return [
                        ...plans,
                    ].sort(
                        (
                            a,
                            b
                        ) =>
                            Number(
                                a?.sortOrder ||
                                0
                            ) -
                            Number(
                                b?.sortOrder ||
                                0
                            )
                    );

                },
                [
                    plans,
                ]
            );


        const openCreate =
            () => {

                setEditingPlan(
                    null
                );

                setForm(
                    buildCreateForm()
                );

                setShowModal(
                    true
                );

            };


        const openEdit =
            (
                plan
            ) => {

                setEditingPlan(
                    plan
                );


                setForm({
                    ...EMPTY_FORM,
                    ...plan,

                    modules:
                        buildFormModules(
                            plan?.modules ||
                            []
                        ),
                });


                setShowModal(
                    true
                );

            };


        const closeModal =
            () => {

                if (
                    saving
                ) {

                    return;

                }


                setShowModal(
                    false
                );

            };


        const handleChange =
            (
                event
            ) => {

                const {
                    name,
                    value,
                    checked,
                    type,
                } =
                    event.target;


                setForm(
                    (
                        previous
                    ) => ({
                        ...previous,

                        [name]:
                            type ===
                            "checkbox"
                                ? checked
                                : type ===
                                    "number"
                                    ? Number(
                                        value
                                    )
                                    : value,
                    })
                );

            };


        const handleModuleChange =
            (
                code
            ) => {

                setForm(
                    (
                        previous
                    ) => ({
                        ...previous,

                        modules:
                            previous.modules.map(
                                (
                                    module
                                ) =>
                                    module.code ===
                                    code
                                        ? {
                                            ...module,

                                            enabled:
                                                !module.enabled,
                                        }
                                        : module
                            ),
                    })
                );

            };


        const handleSubmit =
            async (
                event
            ) => {

                event.preventDefault();


                const action =
                    editingPlan
                        ? updatePlatformSubscriptionPlan({
                            id:
                                editingPlan._id,

                            data:
                                form,
                        })
                        : createPlatformSubscriptionPlan(
                            form
                        );


                const result =
                    await dispatch(
                        action
                    );


                const success =
                    editingPlan
                        ? updatePlatformSubscriptionPlan
                            .fulfilled
                            .match(
                                result
                            )
                        : createPlatformSubscriptionPlan
                            .fulfilled
                            .match(
                                result
                            );


                if (
                    !success
                ) {

                    swal.error({
                        title:
                            "Unable to Save",

                        text:
                            result
                                ?.payload
                                ?.message ||
                            "Subscription plan could not be saved.",
                    });

                    return;

                }


                await swal.success({
                    title:
                        editingPlan
                            ? "Plan Updated"
                            : "Plan Created",

                    text:
                        editingPlan
                            ? "Subscription plan updated successfully."
                            : "Subscription plan created successfully.",
                });


                setShowModal(
                    false
                );


                dispatch(
                    fetchPlatformSubscriptionPlans()
                );

            };


        const handleDelete =
            async (
                plan
            ) => {

                const result =
                    await swal.confirm({
                        title:
                            "Delete Subscription Plan?",

                        text:
                            `${plan.name} will no longer be available for new subscriptions.`,

                        confirmButtonText:
                            "Delete",
                    });


                if (
                    result?.isConfirmed ===
                    false
                ) {

                    return;

                }


                await dispatch(
                    deletePlatformSubscriptionPlan(
                        plan._id
                    )
                );


                dispatch(
                    fetchPlatformSubscriptionPlans()
                );

            };


        /*
        |--------------------------------------------------------------------------
        | SYNC CURRENT TENANT SUBSCRIPTION ACCESS
        |--------------------------------------------------------------------------
        |
        | Updates only module + permission snapshots.
        |
        |--------------------------------------------------------------------------
        */

        const handleSyncTenantAccess =
            async () => {

                const confirmation =
                    await swal.confirm({
                        title:
                            "Sync Tenant Plan Access?",

                        text:
                            "This will refresh module and permission access for all current ACTIVE, TRIAL and PENDING tenant subscriptions from their subscribed plans. Pricing, dates, payment status, limits, features and subscription history will not be changed.",

                        confirmButtonText:
                            "Sync Tenant Access",
                    });


                if (
                    !confirmation
                        ?.isConfirmed
                ) {

                    return;

                }


                const result =
                    await dispatch(
                        syncPlatformTenantPlanAccess()
                    );


                if (
                    !syncPlatformTenantPlanAccess
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    await swal.error({
                        title:
                            "Sync Failed",

                        text:
                            result
                                ?.payload
                                ?.message ||
                            "Unable to synchronize tenant plan access.",
                    });

                    return;

                }


                const summary =
                    result
                        ?.payload
                        ?.data ||
                    {};


                await swal.success({
                    title:
                        "Tenant Access Synced",

                    text:
                        `${Number(
                            summary
                                ?.syncedSubscriptions ||
                            0
                        )} subscription(s) updated, ${Number(
                            summary
                                ?.unchangedSubscriptions ||
                            0
                        )} already up to date, ${Number(
                            summary
                                ?.skippedSubscriptions ||
                            0
                        )} skipped.`,
                });

            };


        return (


                <div className="platform-plan-page">

                    <header className="platform-plan-header">

                        <div>

                            <h1>
                                Subscription Plans
                            </h1>

                            <p>
                                Create and manage pricing, billing periods, usage limits and module access.
                            </p>

                        </div>


                        <div>

                            <button
                                type="button"
                                onClick={
                                    handleSyncTenantAccess
                                }
                                disabled={
                                    syncingTenants
                                }
                                title="Refresh module and permission access for all current tenant subscriptions from their subscribed plans."
                            >

                                <FiRefreshCw />

                                {
                                    syncingTenants
                                        ?
                                        "Syncing..."
                                        :
                                        "Sync Tenant Access"
                                }

                            </button>


                            <button
                                type="button"
                                onClick={() =>
                                    dispatch(
                                        fetchPlatformSubscriptionPlans()
                                    )
                                }
                            >

                                <FiRefreshCw />

                                Refresh

                            </button>


                            <button
                                type="button"
                                className="primary"
                                onClick={
                                    openCreate
                                }
                                disabled={
                                    modulesLoading ||
                                    Boolean(
                                        modulesError
                                    ) ||
                                    moduleOptions.length ===
                                        0
                                }
                            >

                                <FiPlus />

                                Create New Plan

                            </button>

                        </div>

                    </header>


                    <section className="platform-plan-grid">

                        {orderedPlans.map(
                            (
                                plan
                            ) => (

                                <article
                                    key={
                                        plan._id
                                    }
                                    className={`platform-plan-card ${
                                        plan?.isPopular
                                            ? "popular"
                                            : ""
                                    }`}
                                >

                                    <div className="platform-plan-card-top">

                                        <div>

                                            <span>
                                                {plan.code}
                                            </span>

                                            <h2>
                                                {plan.name}
                                            </h2>

                                        </div>


                                        <span
                                            className={`status ${
                                                plan.status ===
                                                "ACTIVE"
                                                    ? "active"
                                                    : ""
                                            }`}
                                        >

                                            {
                                                plan.status
                                            }

                                        </span>

                                    </div>


                                    <p>
                                        {plan.description}
                                    </p>


                                    <div className="platform-plan-price">

                                        {
                                            plan.customPricing
                                                ? (
                                                    <strong>
                                                        Custom Pricing
                                                    </strong>
                                                )
                                                : (
                                                    <>
                                                        <strong>
                                                            ₹
                                                            {
                                                                Number(
                                                                    plan.monthlyPrice ||
                                                                    0
                                                                ).toLocaleString(
                                                                    "en-IN"
                                                                )
                                                            }
                                                        </strong>

                                                        <span>
                                                            / month
                                                        </span>
                                                    </>
                                                )
                                        }

                                    </div>


                                    {!plan.customPricing ? (

                                        <div className="platform-plan-yearly">

                                            ₹
                                            {
                                                Number(
                                                    plan.yearlyPrice ||
                                                    0
                                                ).toLocaleString(
                                                    "en-IN"
                                                )
                                            }

                                            {" "} / year

                                            {
                                                plan.yearlyDiscountPercent
                                                    ? (
                                                        <span>
                                                            Save {plan.yearlyDiscountPercent}%
                                                        </span>
                                                    )
                                                    : null
                                            }

                                        </div>

                                    ) : null}


                                    <div className="platform-plan-limits">

                                        <span>

                                            <FiUsers />

                                            {
                                                plan.maxEmployees ===
                                                null
                                                    ? "Unlimited employees"
                                                    : `${plan.maxEmployees} employees`
                                            }

                                        </span>


                                        <span>

                                            <FiPackage />

                                            {
                                                plan.maxProductListings ===
                                                null
                                                    ? "Unlimited product listings"
                                                    : `${plan.maxProductListings} product listings`
                                            }

                                        </span>

                                    </div>


                                    <div className="platform-plan-modules">

                                        {
                                            (
                                                plan.modules ||
                                                []
                                            )
                                                .filter(
                                                    (
                                                        item
                                                    ) =>
                                                        item.enabled
                                                )
                                                .slice(
                                                    0,
                                                    5
                                                )
                                                .map(
                                                    (
                                                        module
                                                    ) => (

                                                        <span
                                                            key={
                                                                module.code
                                                            }
                                                        >

                                                            <FiCheckCircle />

                                                            {
                                                                module.name
                                                            }

                                                        </span>

                                                    )
                                                )
                                        }

                                    </div>


                                    <div className="platform-plan-actions">

                                        <button
                                            type="button"
                                            onClick={() =>
                                                openEdit(
                                                    plan
                                                )
                                            }
                                        >

                                            <FiEdit2 />

                                            Edit

                                        </button>


                                        <button
                                            type="button"
                                            className="danger"
                                            onClick={() =>
                                                handleDelete(
                                                    plan
                                                )
                                            }
                                        >

                                            <FiTrash2 />

                                        </button>

                                    </div>

                                </article>

                            )
                        )}

                    </section>


                    <BusinessModal
                        open={
                            showModal
                        }

                        title={
                            editingPlan
                                ? "Edit Subscription Plan"
                                : "Create Subscription Plan"
                        }

                        description="Manage pricing, usage limits and module access."

                        size="lg"

                        onClose={
                            closeModal
                        }

                        disableClose={
                            saving
                        }
                    >

                        <form
                            className="platform-plan-form"
                            onSubmit={
                                handleSubmit
                            }
                        >

                            <section>

                                <h3>
                                    Plan Information
                                </h3>


                                <div className="platform-plan-form-grid">

                                    <label>

                                        <span>
                                            Plan Name
                                        </span>

                                        <input
                                            name="name"
                                            value={
                                                form.name
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            required
                                        />

                                    </label>


                                    <label>

                                        <span>
                                            Plan Code
                                        </span>

                                        <input
                                            name="code"
                                            value={
                                                form.code
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            required
                                        />

                                    </label>


                                    <label>

                                        <span>
                                            Plan Type
                                        </span>

                                        <select
                                            name="planType"
                                            value={
                                                form.planType
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        >

                                            <option value="TRIAL">
                                                Trial
                                            </option>

                                            <option value="PAID">
                                                Paid
                                            </option>

                                            <option value="CONTACT">
                                                Contact Business
                                            </option>

                                        </select>

                                    </label>


                                    <label>

                                        <span>
                                            Sort Order
                                        </span>

                                        <input
                                            type="number"
                                            name="sortOrder"
                                            value={
                                                form.sortOrder
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </label>


                                    <label className="full">

                                        <span>
                                            Description
                                        </span>

                                        <textarea
                                            name="description"
                                            value={
                                                form.description
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </label>

                                </div>

                            </section>


                            <section>

                                <h3>
                                    Pricing
                                </h3>


                                <div className="platform-plan-form-grid">

                                    <label>

                                        <span>
                                            Monthly Price
                                        </span>

                                        <input
                                            type="number"
                                            name="monthlyPrice"
                                            value={
                                                form.monthlyPrice
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </label>


                                    <label>

                                        <span>
                                            Yearly Price
                                        </span>

                                        <input
                                            type="number"
                                            name="yearlyPrice"
                                            value={
                                                form.yearlyPrice
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </label>


                                    <label>

                                        <span>
                                            Yearly Discount %
                                        </span>

                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            name="yearlyDiscountPercent"
                                            value={
                                                form.yearlyDiscountPercent
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </label>


                                    <label>

                                        <span>
                                            Trial Days
                                        </span>

                                        <input
                                            type="number"
                                            name="trialDays"
                                            value={
                                                form.trialDays
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </label>

                                </div>

                            </section>


                            <section>

                                <h3>
                                    Usage Limits
                                </h3>


                                <div className="platform-plan-form-grid">

                                    <label>

                                        <span>
                                            Maximum Employees
                                        </span>

                                        <input
                                            type="number"
                                            name="maxEmployees"
                                            value={
                                                form.maxEmployees ??
                                                ""
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </label>


                                    <label>

                                        <span>
                                            Product Listings
                                        </span>

                                        <input
                                            type="number"
                                            name="maxProductListings"
                                            value={
                                                form.maxProductListings ??
                                                ""
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </label>


                                    <label>

                                        <span>
                                            Maximum Branches
                                        </span>

                                        <input
                                            type="number"
                                            name="maxBranches"
                                            value={
                                                form.maxBranches ??
                                                ""
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </label>


                                    <label>

                                        <span>
                                            Storage GB
                                        </span>

                                        <input
                                            type="number"
                                            name="maxStorageGB"
                                            value={
                                                form.maxStorageGB ??
                                                ""
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </label>

                                </div>

                            </section>


                            <section>

                                <h3>
                                    Module Permissions
                                </h3>


                                {
                                    modulesLoading
                                        ?
                                        (
                                            <div>
                                                Loading modules...
                                            </div>
                                        )
                                        :
                                        modulesError
                                            ?
                                            (
                                                <div>
                                                    {
                                                        modulesError
                                                            ?.message ||
                                                        "Unable to load subscription modules."
                                                    }
                                                </div>
                                            )
                                            :
                                            null
                                }


                                <div className="platform-plan-module-grid">

                                    {form.modules.map(
                                        (
                                            module
                                        ) => (

                                            <label
                                                key={
                                                    module.code
                                                }
                                            >

                                                <input
                                                    type="checkbox"
                                                    checked={
                                                        module.enabled
                                                    }
                                                    onChange={() =>
                                                        handleModuleChange(
                                                            module.code
                                                        )
                                                    }
                                                />

                                                <span>
                                                    {module.name}
                                                </span>

                                            </label>

                                        )
                                    )}

                                </div>

                            </section>


                            <section>

                                <h3>
                                    Plan Features
                                </h3>


                                <div className="platform-plan-module-grid">

                                    {[
                                        [
                                            "storefrontEnabled",
                                            "Storefront"
                                        ],
                                        [
                                            "marketplaceEnabled",
                                            "Marketplace"
                                        ],
                                        [
                                            "apiAccessEnabled",
                                            "API Access"
                                        ],
                                        [
                                            "prioritySupport",
                                            "Priority Support"
                                        ],
                                        [
                                            "dedicatedAccountManager",
                                            "Dedicated Account Manager"
                                        ],
                                        [
                                            "availableForSignup",
                                            "Available For Signup"
                                        ],
                                        [
                                            "autoAssignOnTenantRegistration",
                                            "Auto Assign To New Tenant"
                                        ],
                                        [
                                            "isPopular",
                                            "Popular Plan"
                                        ],
                                    ].map(
                                        (
                                            [
                                                field,
                                                label
                                            ]
                                        ) => (

                                            <label
                                                key={
                                                    field
                                                }
                                            >

                                                <input
                                                    type="checkbox"
                                                    name={
                                                        field
                                                    }
                                                    checked={
                                                        Boolean(
                                                            form[field]
                                                        )
                                                    }
                                                    onChange={
                                                        handleChange
                                                    }
                                                />

                                                <span>
                                                    {label}
                                                </span>

                                            </label>

                                        )
                                    )}

                                </div>

                            </section>


                            <div className="platform-plan-form-actions">

                                <button
                                    type="button"
                                    onClick={
                                        closeModal
                                    }
                                    disabled={
                                        saving
                                    }
                                >
                                    Cancel
                                </button>


                                <button
                                    type="submit"
                                    className="primary"
                                    disabled={
                                        saving
                                    }
                                >

                                    {
                                        saving
                                            ? "Saving..."
                                            : editingPlan
                                                ? "Update Plan"
                                                : "Create Plan"
                                    }

                                </button>

                            </div>

                        </form>

                    </BusinessModal>

                </div>


        );

    };


export default PlatformSubscriptionPlanList;